import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useUser } from "@/hooks/queries/useUser";
import { authService } from "@/services";
import useAuth from "@/hooks/stores/useAuth";
import { Menu, X } from "lucide-react";
import logo from "@/assets/images/logo.png";

// Add state management for mobile menu
import { create } from "zustand";

export const useMobileMenu = create((set) => ({
  isOpen: false,
  toggle: () => set((state) => ({ isOpen: !state.isOpen })),
  close: () => set({ isOpen: false }),
}));

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: profile, isLoading, error } = useUser();
  const { userRole } = useAuth();
  const { isOpen, toggle } = useMobileMenu();

  // Add check for registration page
  const isRegistrationPage = window.location.pathname === "/users/new";
  
  // Check if we're on the form page and user is a regular user
  const isFormPage = location.pathname.includes("/form");
  const isUserRole = userRole?.toLowerCase() === "user";
  const isUserDashboard = location.pathname === "/" && isUserRole;

  // Don't render navbar on registration page
  if (isRegistrationPage) {
    return null;
  }

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate("/Login");
    } catch (error) {
      console.error("Navbar: Logout failed:", error);
      navigate("/Login");
    }
  };

  return (
    <header
      className={`fixed top-0 right-0 ${
        userRole?.toLowerCase() === "user" ? "left-0" : "lg:left-[300px] left-0"
      } ${userRole?.toLowerCase() === "user" ? "z-50" : "z-30"}`}
    >
      <div className={`mx-auto w-full ${
        userRole?.toLowerCase() === "user" ? "bg-[#F1EBDB]" : 
        userRole?.toLowerCase() === "legal advisor" ? "bg-[#F1EBDB]" : "bg-card"
      }`}>
        <div
          className={`flex items-center ${
            userRole?.toLowerCase() !== "user" 
              ? "justify-between"
              : "justify-between"
          } shadow-sm border border-border ${
            isUserDashboard ? 'px-8 md:px-16 lg:px-36' : 'px-4 sm:px-6'
          } py-4`}
        >
         
         {(userRole?.toLowerCase() !== "user" || (isUserRole && isFormPage)) && (
            <div className="flex items-center justify-between w-full lg:hidden">
              {/* Logo for mobile view (non-user roles) */}
              <div className="flex items-center">
                <img
                  src={logo}
                  alt="Logo"
                  className="h-8 w-auto"
                />
              </div>

              <button
                onClick={toggle}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-primary hover:text-primary-dark hover:bg-accent/10 transition-colors duration-200"
                aria-label="Toggle menu"
              >
                {isOpen ? (
                  <X size={24} className="text-current" />
                ) : (
                  <Menu size={24} className="text-current" />
                )}
              </button>
            </div>
          )}

          {/* Add logo for user dashboard - show on dashboard, hide on other pages */}
          {userRole?.toLowerCase() === "user" && (
            <div className={`${isUserDashboard ? 'flex' : 'hidden lg:flex'} items-center`}>
              <NavLink to="/">
                <img
                  src={logo}
                  alt="Logo"
                  className="h-8 w-auto"
                />
              </NavLink>
            </div>
          )}

          {/* Navigation links with better mobile handling */}
          <div className="hidden lg:flex items-center space-x-4">
            {userRole?.toLowerCase() === "admin" && (
              <nav className="w-auto sm:w-56 pl-0 sm:pl-4">
                <NavLink
                  to="/"
                  className={({ isActive }) =>
                    `font-medium text-[#F16745] hover:text-[#E55D3D] ${
                      isActive ? "border-none" : ""
                    }`
                  }
                >
                Dashboard
                </NavLink>
              </nav>
            )}
            {userRole?.toLowerCase() === "legal advisor" && (
              <nav className="w-56 pl-4">
                <NavLink
                  to="/"
                  className={({ isActive }) =>
                    `font-medium text-[#F16745] hover:text-[#E55D3D] ${
                      isActive ? "border-none" : ""
                    }`
                  }
                >
                Dashboard
                </NavLink>
              </nav>
            )}
            {userRole?.toLowerCase() === "head of department" && (
              <nav className="w-56 pl-4">
                <NavLink
                  to="/"
                  className={({ isActive }) =>
                    `font-medium ${isActive ? "border-none" : ""}`
                  }
                >
                Dashboard
                </NavLink>
              </nav>
            )}
            {userRole?.toLowerCase() === "reporting" && (
              <nav className="w-56 pl-4">
                <NavLink
                  to="/reports"
                  className={({ isActive }) =>
                    `font-medium text-[#F16745] hover:text-[#E55D3D] ${
                      isActive ? "border-none" : ""
                    }`
                  }
                >
                  Reporting
                </NavLink>
              </nav>
            )}
            {(userRole?.toLowerCase() === "manager" || userRole?.toLowerCase() === "bereavement team") && (
              <nav className="w-56 pl-4">
                <NavLink
                  to="/"
                  className={({ isActive }) =>
                    `font-medium text-[#F16745] hover:text-[#E55D3D] ${
                      isActive ? "border-none" : ""
                    }`
                  }
                >
                  Dashboard
                </NavLink>
              </nav>
            )}
          </div>

          {/* Right side actions */}
          <div className={`flex items-center ${
            userRole?.toLowerCase() === "user" && !isFormPage 
              ? "ml-auto space-x-6 h-8"
              : "ml-auto gap-3 sm:gap-6  md:pr-0"
          }`}>
            {userRole?.toLowerCase() === "user" && (
              <>
                <NavLink
                  to="/"
                  className={({ isActive }) =>
                    `${isUserDashboard ? 'flex' : 'hidden lg:flex'} items-center relative font-medium text-[#F16745] hover:text-[#E55D3D] transition-colors text-sm sm:text-base py-2 ${
                      isActive ? 'after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-[#F16745]' : ''
                    }`
                  }
                >
                  Dashboard
                </NavLink>
                <button
                  onClick={handleLogout}
                  className={`${isUserDashboard ? 'flex' : 'hidden lg:flex'} items-center font-medium text-[#F16745] hover:text-[#E55D3D] transition-colors text-sm sm:text-base py-2`}
                >
                  Logout
                </button>
              </>
            )}
            {userRole?.toLowerCase() !== "user" && (
              <button
                onClick={handleLogout}
                className="hidden lg:block text-sm sm:text-base font-medium text-[#F16745] hover:text-[#E55D3D] transition-colors px-4 sm:px-5"
              >
                Logout
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

const SubmenuItem = ({ link, name, onClick }) => {
  return (
    <a
      href={link}
      onClick={(e) => {
        e.preventDefault();
        if (onClick) onClick();
      }}
      className="block rounded px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
    >
      {name}
    </a>
  );
};